import { Injectable, inject } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpClient
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, finalize, map, tap, shareReplay } from 'rxjs/operators';
import { ShellDomainProvider } from '../standalone-packages/ecap3/core';
import { UrlResolverService } from '../services/url/url-resolver.service';

@Injectable()
export class IpexFileTokenInterceptor implements HttpInterceptor {
    private readonly urlResolver = inject(UrlResolverService);
    private readonly TOKEN_STORAGE_KEY: string = 'ipex_token';
    private tokenRequestInProgress: Observable<string> | null = null;

    private readonly ipexQueryApiBaseUrl: string = this.urlResolver.getExternalIpexUrl();
    private readonly ipexFileServiceBaseUrl: string = `${ShellDomainProvider['api'].IpexFileService}/`;
    private readonly ipexDataBeastServiceBaseUrl: string = `${ShellDomainProvider['api'].IpexDataBeastService}/`;

    private readonly commonHttpOptions: any = {
        headers: {
            'Content-Type': 'application/json'
        },
        withCredentials: true
    };

    constructor(private readonly httpClient: HttpClient) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if this is an IPEX file service request
        if (!this.isIpexFileServiceRequest(request)) {
            return next.handle(request);
        }

        // Get stored token
        const token = this.getStoredToken();

        // If token exists, use it; otherwise fetch a new one
        if (token) {
            return this.handleRequest(request, next, token);
        } else {
            // No token available, fetch one first
            return this.getIpexToken().pipe(
                switchMap((newToken: string) => {
                    return this.handleRequest(request, next, newToken);
                })
            );
        }
    }

    private handleRequest(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
        request = this.addTokenToRequest(request, token);

        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.isIpexFileServiceRequest(request)) {
                    return this.handle401Error(request, next);
                }
                return throwError(() => error);
            })
        );
    }

    private isIpexFileServiceRequest(request: HttpRequest<any>): boolean {
        return (
            request.url.startsWith(this.ipexFileServiceBaseUrl) ||
            request.url.startsWith(this.ipexDataBeastServiceBaseUrl)
        );
    }

    private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.getIpexToken().pipe(
            switchMap((newToken: string) => {
                return next.handle(this.addTokenToRequest(request, newToken));
            })
        );
    }

    private getIpexToken(): Observable<string> {
        // If a token request is already in progress, return that observable
        if (this.tokenRequestInProgress) {
            return this.tokenRequestInProgress;
        }

        const tokenEndpoint = `${this.ipexQueryApiBaseUrl}GetIpexToken`;

        this.tokenRequestInProgress = this.httpClient.get(tokenEndpoint, this.commonHttpOptions).pipe(
            map((response: any) => {
                if (!response?.access_token) {
                    throw new Error('No access token found in response');
                }
                return response.access_token;
            }),
            tap(token => {
                this.storeToken(token);
            }),
            catchError(error => {
                console.error('Failed to fetch IPEX token:', error);
                this.clearToken();
                return throwError(() => error);
            }),
            finalize(() => {
                this.tokenRequestInProgress = null;
            }),
            // shareReplay(1) is used to:
            // 1. Cache the last emitted token value
            // 2. Share the same token request with multiple subscribers
            // 3. Prevent multiple simultaneous token requests
            // This ensures that if multiple HTTP requests need a new token simultaneously,
            // only one token request is made to the server
            shareReplay(1)
        );

        return this.tokenRequestInProgress;
    }

    private getStoredToken(): string | null {
        try {
            return localStorage.getItem(this.TOKEN_STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to get token from localStorage:', error);
            return null;
        }
    }

    private storeToken(token: string): void {
        try {
            localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
        } catch (error) {
            console.warn('Failed to store token in localStorage:', error);
        }
    }

    private clearToken(): void {
        try {
            localStorage.removeItem(this.TOKEN_STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to clear token from localStorage:', error);
        }
    }
}
